import React, { useEffect, useState } from "react";
import { Card, Typography, Button, Spin, message } from "antd";
import styled from "@emotion/styled";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useNavigate } from "react-router-dom";
import { URL } from "../../helper/Mainurl";
import { apiClient } from "../../utils/apiClient";
const { Title, Text } = Typography;
const SummaryCard = styled(Card)`
  margin-top: 62px;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  .ant-card-head {
    border-bottom: none;
    padding: 16px 24px;
  }
  .ant-card-body {
    padding: 0 24px 24px;
  }

  @media (max-width: 768px) {
    margin-top: 0;

    .ant-card-head {
      padding: 12px 16px;
    }

    .ant-card-body {
      padding: 0 16px 16px;
    }
  }
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  flex: 1;
  background-color: ${(props) => props.bgColor};
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-width: 180px;

  @media (max-width: 576px) {
    width: 100%;
    min-width: unset;
  }
`;

const StatValue = styled.div`
  font-size: 22.8px;
  font-weight: 700;
  color: ${(props) => props.Color || "#fff"};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 15.2px;
  color: #54536e;
`;

const TaskListContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const TaskBox = styled.div`
  border: 1.5px solid #e1e6ef;
  border-radius: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 40%;
  max-width: 320px;
  min-width: 260px;
  padding: 16px;

  @media (max-width: 992px) {
    width: calc(50% - 12px);
    min-width: unset;
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const TaskBoxHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e1e6ef;
`;

const TaskBoxValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
`;

const TaskBoxSubtext = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-top: 4px;
`;

const UpChange = styled.span`
  background: #defdec;
  color: #3eb9af;
  display: flex;
  align-items: center;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 13px;
  padding: 2px 8px 2px 4px;
  margin-left: 4px;
`;

const DownChange = styled.span`
  background: #fff0f0;
  color: #ff2828;
  display: flex;
  align-items: center;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 13px;
  padding: 2px 8px 2px 4px;
  margin-left: 4px;
`;

const TasksSummary = () => {
  const [taskData, setTaskData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get(`/v1/task/summary`);
        const json = response.data;
        setTaskData(json.data.task_summary);
      } catch (error) {
        message.error("Failed to load task summary.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const renderChangeBadge = (change) => {
    if (change > 0) {
      return (
        <UpChange>
          <ArrowUpwardIcon style={{ fontSize: 18, marginRight: 2 }} />
          {change}%
        </UpChange>
      );
    } else if (change < 0) {
      return (
        <DownChange>
          <ArrowDownwardIcon style={{ fontSize: 18, marginRight: 2 }} />
          {Math.abs(change)}%
        </DownChange>
      );
    } else {
      return <span style={{ marginLeft: 6, color: "#999" }}>0%</span>;
    }
  };

  // Get data for each status
  const completed =
    taskData.find((item) => item.task_status === "Completed") || {};
  const pending = taskData.find((item) => item.task_status === "Pending") || {};
  const notStarted =
    taskData.find((item) => item.task_status === "Not Started") || {};

  // Calculate summary stats
  const closedCount =
    (Number(completed.current_month_count) || 0) +
    (Number(completed.last_month_count) || 0);
  const openCount =
    (Number(pending.current_month_count) || 0) +
    (Number(pending.last_month_count) || 0);
  const newCount =
    (Number(notStarted.current_month_count) || 0) +
    (Number(notStarted.last_month_count) || 0);

  return (
    <SummaryCard
      title={
        <Title
          level={4}
          style={{
            margin: 0,
            color: "#54536E",
            fontSize: 22.8,
            fontWeight: 600,
          }}
        >
          Tasks Summary
        </Title>
      }
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <StatsContainer>
            <StatCard bgColor="#ECFDF5">
              <StatValue Color="#40997E">{closedCount}</StatValue>
              <StatLabel>Closed</StatLabel>
            </StatCard>

            <StatCard bgColor="#FFFBEB">
              <StatValue Color="#E0A23A">{openCount}</StatValue>
              <StatLabel>Open</StatLabel>
            </StatCard>

            <StatCard bgColor="#FEF2F2">
              <StatValue Color="#FF3737">{newCount}</StatValue>
              <StatLabel>New</StatLabel>
            </StatCard>
          </StatsContainer>

          <TaskListContainer>
            <TaskBox>
              <TaskBoxHeader>
                <Text style={{ color: "#54536E", fontWeight: 500 }}>
                  Completed Tasks
                </Text>
                <Button
                  type="link"
                  onClick={() => navigate("/calender?status=completed")}
                  icon={
                    <ArrowForwardIosIcon
                      style={{ fontSize: 20, color: "#1D2433" }}
                    />
                  }
                  style={{
                    border: "1.5px solid #E1E6EF",
                    borderRadius: "50%",
                    padding: "6px",
                  }}
                />
              </TaskBoxHeader>
              <div style={{ padding: "10px" }}>
                <TaskBoxValue>
                  {completed.current_month_count ?? 0}
                </TaskBoxValue>
                <TaskBoxSubtext>
                  {completed.last_month_count ?? 0} Last month
                  {renderChangeBadge(completed.percent_change)}
                </TaskBoxSubtext>
              </div>
            </TaskBox>

            <TaskBox>
              <TaskBoxHeader>
                <Text style={{ color: "#54536E", fontWeight: 500 }}>
                  Pending Tasks
                </Text>
                <Button
                  type="link"
                  onClick={() => navigate("/calender?status=pending")}
                  icon={
                    <ArrowForwardIosIcon
                      style={{ fontSize: 20, color: "#1D2433" }}
                    />
                  }
                  style={{
                    border: "1.5px solid #E1E6EF",
                    borderRadius: "50%",
                    padding: "6px",
                  }}
                />
              </TaskBoxHeader>
              <div style={{ padding: "10px" }}>
                <TaskBoxValue>{pending.current_month_count ?? 0}</TaskBoxValue>
                <TaskBoxSubtext>
                  {pending.last_month_count ?? 0} Last month
                  {renderChangeBadge(pending.percent_change)}
                </TaskBoxSubtext>
              </div>
            </TaskBox>
          </TaskListContainer>
        </>
      )}
    </SummaryCard>
  );
};

export default TasksSummary;
